/**
 * Adapter for converting between Gongfu YAML task format and unified format
 */

import { BaseAdapter, UnifiedTask, TaskMetadata } from './base-adapter.js';

// Gongfu task definition (from apps/cli)
export interface GongfuTask {
  id: string;
  name: string;
  type?: 'feature' | 'bug' | 'test' | 'doc' | 'refactor';
  description?: string;
  status: 'todo' | 'doing' | 'review' | 'done';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  owner?: string;
  assignee?: string;
  tags?: string[];
  est?: string; // estimate like "3d", "2h"
  actual?: string;
  dependsOn?: string[];
  created?: string;
  updated?: string;
  completed?: string;
  notes?: string;
}

export class GongfuAdapter extends BaseAdapter<GongfuTask, UnifiedTask> {
  protected readonly sourceFormat = 'gongfu-yaml';
  protected readonly targetFormat = 'unified';
  
  /**
   * Convert Gongfu task to unified format
   */
  toUnified(gongfuTask: GongfuTask): UnifiedTask {
    if (!this.validateSource(gongfuTask)) {
      this.handleError(new Error('Invalid Gongfu task'), 'toUnified');
    }
    
    const now = new Date();
    
    return {
      // Core fields
      id: gongfuTask.id,
      title: gongfuTask.name,
      description: gongfuTask.description || gongfuTask.notes,
      status: this.mapStatus(gongfuTask.status),
      priority: gongfuTask.priority || 'normal',
      
      // Extended fields
      assignee: gongfuTask.assignee || gongfuTask.owner,
      tags: gongfuTask.tags || [],
      estimate: gongfuTask.est,
      dependencies: gongfuTask.dependsOn || [],
      
      // Gongfu-specific fields preserved
      type: gongfuTask.type,
      owner: gongfuTask.owner,
      
      // Metadata
      metadata: {
        source: 'gongfu',
        format: 'yaml',
        version: '1.0',
        created: gongfuTask.created ? new Date(gongfuTask.created) : now,
        updated: gongfuTask.updated ? new Date(gongfuTask.updated) : now,
      },
      
      // Timestamps
      created: gongfuTask.created ? new Date(gongfuTask.created) : now,
      updated: gongfuTask.updated ? new Date(gongfuTask.updated) : now,
      completed: gongfuTask.completed ? new Date(gongfuTask.completed) : undefined,
    };
  }
  
  /**
   * Convert unified task back to Gongfu format
   */
  fromUnified(unified: UnifiedTask): GongfuTask {
    if (!this.validateUnified(unified)) {
      this.handleError(new Error('Invalid unified task'), 'fromUnified');
    }
    
    return {
      id: unified.id,
      name: unified.title,
      type: unified.type,
      description: unified.description,
      status: this.reverseMapStatus(unified.status),
      priority: unified.priority,
      owner: unified.owner || unified.assignee,
      assignee: unified.assignee,
      tags: unified.tags,
      est: unified.estimate,
      dependsOn: unified.dependencies,
      created: unified.created.toISOString(),
      updated: unified.updated.toISOString(),
      completed: unified.completed?.toISOString(),
    };
  }
  
  /**
   * Map Gongfu status to unified status
   */
  private mapStatus(gongfuStatus: string): UnifiedTask['status'] {
    const statusMap: Record<string, UnifiedTask['status']> = {
      'todo': 'todo',
      'doing': 'in_progress',
      'review': 'review',
      'done': 'done',
    };
    
    return statusMap[gongfuStatus] || 'todo';
  }
  
  /**
   * Map unified status back to Gongfu status
   */
  private reverseMapStatus(unifiedStatus: UnifiedTask['status']): GongfuTask['status'] {
    const statusMap: Record<UnifiedTask['status'], GongfuTask['status']> = {
      'todo': 'todo',
      'in_progress': 'doing',
      'review': 'review',
      'done': 'done',
    };
    
    return statusMap[unifiedStatus];
  }
}